import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { InputNumber, Button, Drawer, Icon, Spin, Divider } from 'antd';
import produce from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmailSubscription } from 'src/components/common/EmailSubscription';
import { QaError } from '../../types/QaErrors';
import { AuditModal, AuditDetails } from '../AuditModal';
import { ExcludeApps } from './ExcludeApps';
import { Info } from './Info';
import { ConvertedScriptConfiguration } from '../../types/scriptConfiguration';
import useScriptConfiguration from '../../hooks/useScriptConfiguration';
import styles from './ConfigModal.module.less';

const loadingIcon = <Icon type="loading" spin />;

enum ColumnName {
  manualPriority = 'manual_priority',
  subscriber = 'subscriber',
  appErrorInfo = 'exclude_error_codes',
  manualErrorThreshold = 'manual_error_threshold',
}

type ConfigModalProps = {
  qaError: QaError;
  visible: boolean;
  onClose: () => void;
  onComplete: () => void;
};

type InputNumberOverrideProps = {
  value: number;
  initialValue: number;
  placeholder: (value: number) => string;
  onChange: (value?: number) => void;
  max: number;
  disabledSave: boolean;
  setDisabledSave: (disabled: boolean) => void;
};

type FormRowProps = {
  label: string;
  auditTitle: string;
  columnName: string;
  errorId: string | null;
  setAuditDetails: (value: AuditDetails) => void;
  children: ReactNode;
};

const InputNumberOverride: React.FC<InputNumberOverrideProps> = ({
  value,
  initialValue,
  placeholder,
  onChange,
  max,
  setDisabledSave,
}) => {
  const [error, setError] = useState<string | null>(null);
  const priorityOverrideMaxNum = 99999;
  const handleInputChange = (val: string | number | null | undefined) => {
    if (!val) {
      onChange(0);
      setError(null);
      return;
    }
    const numValue = parseInt(val.toString(), 10);
    if (isNaN(numValue)) {
      setError('Please enter a valid number.');
      setDisabledSave(true);
      return;
    }
    if (!/^\d+$/.test(val.toString())) {
      setError('Please enter a valid number.');
      setDisabledSave(true);
      return;
    }
    if (max && numValue > max) {
      setError(
        `${
          max === priorityOverrideMaxNum && numValue > max
            ? 'Priority Override cannot be more than 5 digits'
            : `Error Threshold Override cannot be more than ${max}.`
        }`
      );
      setDisabledSave(true);
      onChange(numValue);
    } else {
      onChange(numValue);
      setError(null);
      setDisabledSave(false);
    }
  };
  const isDefault = value === initialValue;
  return (
    <>
      <InputNumber
        className={styles.input}
        min={initialValue}
        value={value}
        placeholder={placeholder(initialValue)}
        formatter={(value) => (isDefault ? '' : String(value ?? ''))}
        onChange={handleInputChange}
      />
      {error && <div style={{ color: 'red' }}>{error}</div>}
    </>
  );
};

const FormRow: React.FC<FormRowProps> = ({ label, auditTitle, columnName, errorId, setAuditDetails, children }) => {
  const handleShowAuditList = () =>
    setAuditDetails({
      title: auditTitle,
      columnName,
      scriptErrorConfigId: errorId,
    });

  return (
    <div className={styles.item}>
      <div className={styles.xRow}>
        <label className={styles.label}>{label}</label>
        <label className={styles.linkToAudit} onClick={handleShowAuditList}>
          Audit
        </label>
      </div>
      {children}
    </div>
  );
};

export const ConfigModal: React.FC<ConfigModalProps> = ({ qaError, visible, onClose, onComplete }) => {
  const [{ data, loading }, saveScriptConfiguration] = useScriptConfiguration({
    appId: qaError.applicationId,
    siteCode: qaError.siteCode,
    scriptName: qaError.scriptName,
  });
  const initScriptConfiguration: ConvertedScriptConfiguration = {
    scriptErrorConfigId: data?.scriptErrorConfigId ?? null,
    appId: qaError.applicationId,
    siteCode: qaError.siteCode,
    scriptName: qaError.scriptName,
    scriptHealth: 0,
    priorityOverride: 0,
    errorThresholdOverride: 0,
    emailSubscription: [],
    excludeErrorCodes: [],
    generalErrorCodes: [],
  };
  const [script, setScript] = useState<ConvertedScriptConfiguration>(initScriptConfiguration);

  const [auditDetails, setAuditDetails] = useState<AuditDetails | null>(null);
  const editableScript = useRef(script);
  const isFirstRender = useRef<boolean>(true);
  const isUnchanged = script === editableScript.current;
  const [disabledSave, setDisabledSave] = useState<boolean>(false);

  const mergeState = (fn: (draft: ConvertedScriptConfiguration) => void) => {
    setScript(produce(fn));
  };

  const setPriorityOverride = (value?: number) => {
    if (value !== undefined) {
      mergeState((draft) => {
        draft.priorityOverride = value;
      });
    }
  };
  const setErrorThresholdOverride = (value?: number) => {
    if (value !== undefined) {
      mergeState((draft) => {
        draft.errorThresholdOverride = value;
      });
    }
  };

  const setErrorCodes = (value: string[]) => {
    mergeState((draft) => {
      draft.excludeErrorCodes = value;
    });
  };

  const setEmailSubscription = (emails: string[]) => {
    mergeState((draft) => {
      draft.emailSubscription = emails;
    });
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  useEffect(() => {
    if (!loading && !isFirstRender.current) {
      setScript((prev) => {
        editableScript.current = prev;
        return data || initScriptConfiguration;
      });
    }

    isFirstRender.current = false;
  }, [loading]);

  return (
    <Drawer onClose={onClose} visible={visible} placement="bottom" closable={false} height={'100%'}>
      <div className={styles.header}>
        <h1 className={styles.title}>Script Configuration</h1>
        <Button className={styles.btnClose} onClick={onClose} type="link">
          <FontAwesomeIcon icon={['fal', 'times']} size={'3x'} />
        </Button>
      </div>
      <div className={styles.content}>
        <div className={styles.innerContent}>
          <Info loading={loading} qaError={qaError} />
          {loading ? (
            <Spin indicator={loadingIcon} />
          ) : (
            <>
              <FormRow
                label={'Priority Override'}
                auditTitle={'Priority Override Audit'}
                columnName={ColumnName['manualPriority']}
                errorId={script.scriptErrorConfigId}
                setAuditDetails={setAuditDetails}
              >
                <InputNumberOverride
                  value={script.priorityOverride}
                  initialValue={initScriptConfiguration.priorityOverride}
                  placeholder={(_value) => `Enter Priority (Default ${parseInt(qaError.priority, 10)})`}
                  onChange={setPriorityOverride}
                  max={99999}
                  disabledSave={disabledSave}
                  setDisabledSave={setDisabledSave}
                />
              </FormRow>
              <FormRow
                label={'Error Threshold Override'}
                auditTitle={'Error Threshold Override Audit'}
                columnName={ColumnName['manualErrorThreshold']}
                errorId={script.scriptErrorConfigId}
                setAuditDetails={setAuditDetails}
              >
                <InputNumberOverride
                  value={script.errorThresholdOverride}
                  initialValue={initScriptConfiguration.errorThresholdOverride}
                  placeholder={(_value) => `Enter Error Threshold (Default ${parseInt(qaError.errorThreshold, 10)})`}
                  onChange={setErrorThresholdOverride}
                  max={100}
                  disabledSave={disabledSave}
                  setDisabledSave={setDisabledSave}
                />
              </FormRow>
              <Divider className={styles.divider} />
              <FormRow
                label={'Error Code Exclusion'}
                auditTitle={'Error Codes Audit'}
                columnName={ColumnName['appErrorInfo']}
                errorId={script.scriptErrorConfigId}
                setAuditDetails={setAuditDetails}
              >
                <ExcludeApps
                  generalErrorCodes={script.generalErrorCodes}
                  errorCodes={script.excludeErrorCodes}
                  onChangeErrorCodes={setErrorCodes}
                />
              </FormRow>
              <FormRow
                label={'Email Subscription'}
                auditTitle={'Email Subscription Audit'}
                columnName={ColumnName['subscriber']}
                errorId={script.scriptErrorConfigId}
                setAuditDetails={setAuditDetails}
              >
                <EmailSubscription
                  subscriptions={script.emailSubscription}
                  placeholder="Enter email and press Enter"
                  setSubscribers={setEmailSubscription}
                />
              </FormRow>
              <div className={styles.xCenter}>
                <Button
                  type="primary"
                  disabled={isUnchanged || disabledSave}
                  className={styles.buttonSave}
                  onClick={() => saveScriptConfiguration(script, handleComplete)}
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      {auditDetails && <AuditModal auditDetails={auditDetails} onClose={() => setAuditDetails(null)} />}
    </Drawer>
  );
};
